import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, ChevronUp, ChevronDown, X, Award, BookOpen, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CategoryFilter from './CategoryFilter';
import { leitnerService } from '../../services/leitnerService';

const TestCreatorView = ({ questions, categories, addTest, onCancel, bookId }) => {
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [timeLimitPerQuestion, setTimeLimitPerQuestion] = useState(30);
  const [passingScore, setPassingScore] = useState(70);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Category filter state
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [loadedCategories, setLoadedCategories] = useState(categories || { subjects: [], grades: [], topics: [] });
  
  // Scheduling
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  
  // Test Categories (what the test is tagged as)
  const [testSubject, setTestSubject] = useState('');
  const [testGrade, setTestGrade] = useState('');
  const [testTopics, setTestTopics] = useState([]);

  // Load categories if not provided
  useEffect(() => {
    const loadCategories = async () => {
      if (!categories && bookId) {
        try {
          const cats = await leitnerService.getQuestionCategories(bookId);
          setLoadedCategories(cats);
        } catch (error) {
          console.error('Failed to load categories:', error);
        }
      }
    };
    loadCategories();
  }, [bookId, categories]);

  // Filter questions using the same logic as CategoryFilter
  const filteredQuestions = useMemo(() => {
    return leitnerService.filterQuestionsByCategories(questions, filters);
  }, [questions, filters]);

  const availableQuestions = filteredQuestions.filter(q => !selectedQuestionIds.includes(q.id));

  console.log('Available question', availableQuestions);

  const handleAddQuestion = (id) => {
    if (!selectedQuestionIds.includes(id)) {
      setSelectedQuestionIds(prev => [...prev, id]);
    }
  };

  const handleRemoveQuestion = (id) => {
    setSelectedQuestionIds(prev => prev.filter(qId => qId !== id));
  };

  const moveQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === selectedQuestionIds.length - 1)) {
      return;
    }
    const newIds = [...selectedQuestionIds];
    const idToMove = newIds[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    newIds[index] = newIds[swapIndex];
    newIds[swapIndex] = idToMove;
    setSelectedQuestionIds(newIds);
  };

  const handleTopicToggle = (topic) => {
    if (testTopics.includes(topic)) {
      setTestTopics(testTopics.filter(t => t !== topic));
    } else {
      setTestTopics([...testTopics, topic]);
    }
  };

  const calculateTotalMarks = () => {
    return selectedQuestionIds.reduce((total, id) => {
      const q = questions.find(question => question.id === id);
      if (!q) return total;
      
      if (q.type === 'stem' && q.subQuestions) {
        return total + (q.marks || q.subQuestions.length);
      }
      return total + (q?.marks || 1);
    }, 0);
  };

  const getQuestionCount = () => {
    return selectedQuestionIds.reduce((count, id) => {
      const q = questions.find(question => question.id === id);
      if (!q) return count;
      
      if (q.type === 'stem' && q.subQuestions) {
        return count + q.subQuestions.length;
      }
      return count + 1;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!name.trim()) {
      setError('Test name cannot be empty.');
      return;
    }
    if (selectedQuestionIds.length === 0) {
      setError('You must select at least one question for the test.');
      return;
    }
    
    if (enableSchedule && scheduledDate && scheduledTime) {
      const startDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (startDateTime < new Date()) {
        setError('Scheduled start time cannot be in the past.');
        return;
      }
      
      if (endDate && endTime) {
        const endDateTime = new Date(`${endDate}T${endTime}`);
        if (endDateTime <= startDateTime) {
          setError('End time must be after start time.');
          return;
        }
      }
    }
    
    setIsSubmitting(true);
    try {
      const testData = {
        name,
        instructions: instructions.trim() || null,
        questionIds: selectedQuestionIds,
        timeLimitPerQuestion: Number(timeLimitPerQuestion) || 0,
        passingScore: Number(passingScore) > 0 ? Number(passingScore) : undefined,
        totalMarks: calculateTotalMarks(),
        questionCount: getQuestionCount(),
        subject: testSubject || null,
        grade: testGrade || null,
        topics: testTopics,
        schedule: enableSchedule && scheduledDate && scheduledTime ? {
          startDateTime: new Date(`${scheduledDate}T${scheduledTime}`).toISOString(),
          endDateTime: endDate && endTime ? new Date(`${endDate}T${endTime}`).toISOString() : null,
          isScheduled: true
        } : null
      };
      
      await addTest(testData);
    } catch (err) {
      setError('Failed to create test. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedQuestions = selectedQuestionIds.map(id => questions.find(q => q.id === id)).filter(q => !!q);

  const getQuestionText = (q) => q.type === 'stem' ? q.stemText : q.text;

  const getTypeBadge = (q) => {
    if (q.type === 'stem') {
      return (
        <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
          STEM ({q.subQuestions?.length || 0})
        </span>
      );
    }
    return null;
  };

  return (
    <div className="bg-slate-800 p-6 rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-indigo-400">Create New Test</h2>
        <button onClick={onCancel} className="text-slate-400 hover:text-white">
          <X size={24} />
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded-md text-red-200">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">Test Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Chapter 5 Assessment"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Time per Question (sec)</label>
            <input
              type="number"
              value={timeLimitPerQuestion}
              onChange={(e) => setTimeLimitPerQuestion(Number(e.target.value))}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
              placeholder="0 for no limit"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Passing Score (%)</label>
            <input
              type="number"
              value={passingScore}
              onChange={(e) => setPassingScore(Number(e.target.value))}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., 70"
              min="1"
              max="100"
            />
          </div>
        </div>

        {/* Test Instructions */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
            <FileText size={16} />
            Test Instructions (Optional)
          </label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
            className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter instructions for test takers... e.g., 'Read each question carefully. You may use a calculator for Section B.'"
          />
        </div>

        {/* Test Categories */}
        <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
          <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
            <BookOpen size={16} />
            Test Categories (Tag this test)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Subject</label>
              <select
                value={testSubject}
                onChange={(e) => setTestSubject(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
              >
                <option value="">Select Subject</option>
                {loadedCategories.subjects?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Grade</label>
              <select
                value={testGrade}
                onChange={(e) => setTestGrade(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
              >
                <option value="">Select Grade</option>
                {loadedCategories.grades?.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Topics</label>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {loadedCategories.topics?.slice(0, 10).map(topic => (
                  <button
                    key={topic}
                    type="button"
                    onClick={() => handleTopicToggle(topic)}
                    className={`px-2 py-0.5 text-xs rounded transition ${
                      testTopics.includes(topic)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scheduling */}
        <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
          <label className="flex items-center gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={enableSchedule}
              onChange={(e) => setEnableSchedule(e.target.checked)}
              className="rounded bg-slate-600 border-slate-500 text-indigo-500"
            />
            <span className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Calendar size={16} />
              Schedule Test
            </span>
          </label>
          
          {enableSchedule && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Start Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">End Date (Optional)</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">End Time (Optional)</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-sm text-slate-100"
                />
              </div>
            </div>
          )}
        </div>

        {/* Question Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Available Questions */}
          <div>
            <h3 className="text-lg font-medium text-slate-300 mb-3 flex items-center justify-between">
              <span>Available Questions</span>
              <span className="text-sm text-slate-500">{availableQuestions.length} questions</span>
            </h3>
            
            {/* Category Filter */}
            <div className="mb-3">
              <CategoryFilter
                categories={loadedCategories}
                selectedFilters={filters}
                onFilterChange={setFilters}
                onClear={() => setFilters({ subject: null, grade: null, topics: [] })}
                questions={questions}
              />
            </div>
            
            <div className="space-y-2 max-h-[40vh] overflow-y-auto border border-slate-700 rounded-md p-2 bg-slate-900/50">
              {availableQuestions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {questions.length === 0 ? 'No questions available.' : 'No questions match the selected filters.'}
                </p>
              ) : (
                availableQuestions.map(q => (
                  <div
                    key={q.id}
                    onClick={() => handleAddQuestion(q.id)}
                    className="p-3 bg-slate-700 rounded-md cursor-pointer hover:bg-slate-600 transition"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm text-slate-200 line-clamp-2 flex-1">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                          {getQuestionText(q)}
                        </ReactMarkdown>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {getTypeBadge(q)}
                        {q.marks > 1 && (
                          <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded flex items-center gap-0.5">
                            <Award size={10} />
                            {q.marks}
                          </span>
                        )}
                        <span className="text-xs text-indigo-400">+ Add</span>
                      </div>
                    </div>
                    {(q.subject || q.grade) && (
                      <div className="flex gap-1 mt-1">
                        {q.subject && <span className="text-xs text-slate-500">{q.subject}</span>}
                        {q.grade && <span className="text-xs text-slate-500">• {q.grade}</span>}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Selected Questions */}
          <div>
            <h3 className="text-lg font-medium text-slate-300 mb-3 flex items-center justify-between">
              <span>Selected Questions</span>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-slate-500">{selectedQuestionIds.length} items ({getQuestionCount()} Qs)</span>
                <span className="text-amber-400 flex items-center gap-1">
                  <Award size={14} />
                  {calculateTotalMarks()} marks
                </span>
              </div>
            </h3>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-slate-700 rounded-md p-2 bg-slate-900/50">
              {selectedQuestions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Click questions to add them.</p>
              ) : (
                selectedQuestions.map((q, index) => (
                  <div key={q.id} className="flex items-center gap-2 p-3 bg-indigo-900/30 rounded-md border border-indigo-700">
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, 'up')}
                        disabled={index === 0}
                        className="p-0.5 text-slate-400 hover:text-white disabled:opacity-30"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, 'down')}
                        disabled={index === selectedQuestions.length - 1}
                        className="p-0.5 text-slate-400 hover:text-white disabled:opacity-30"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                    <span className="text-xs text-slate-500 w-6">{index + 1}.</span>
                    <div className="flex-1">
                      <div className="text-sm text-slate-200 line-clamp-2">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>
                          {getQuestionText(q)}
                        </ReactMarkdown>
                      </div>
                      {q.type === 'stem' && (
                        <span className="text-xs text-purple-400">
                          Stem with {q.subQuestions?.length || 0} sub-questions
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                        {q.marks || (q.type === 'stem' ? q.subQuestions?.length : 1)}pts
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveQuestion(q.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-md transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || selectedQuestionIds.length === 0}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-bold rounded-md transition"
          >
            {isSubmitting ? 'Creating...' : 'Create Test'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TestCreatorView;